import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, documentsAPI } from '../api/client';
import ChatInterface from '../components/Chat/ChatInterface';
import DocumentUpload, { DocumentList } from '../components/Documents/DocumentUpload';
import {
  ArrowLeftIcon, DocumentTextIcon, ChatBubbleLeftRightIcon,
  ArrowsPointingInIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export default function ProjectView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState('chat');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const pollRef = useRef(null);

  // ESC closes full-screen mode
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded]);

  // ── initial load ─────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      projectsAPI.get(projectId),
      documentsAPI.list(projectId),
    ]).then(([projRes, docsRes]) => {
      setProject(projRes.data);
      setDocuments(docsRes.data);
    }).catch(() => toast.error('Failed to load project'))
      .finally(() => setLoading(false));
  }, [projectId]);

  // ── poll while any document is still processing ───────────────
  useEffect(() => {
    const hasUnprocessed = documents.some((d) => !d.processed);

    if (!hasUnprocessed) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    if (pollRef.current) return; // already polling

    pollRef.current = setInterval(async () => {
      try {
        const res = await documentsAPI.list(projectId);
        setDocuments(res.data);
        if (res.data.every((d) => d.processed)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.success('All documents indexed and ready!');
        }
      } catch {
        // swallow — keep trying
      }
    }, 3000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [documents, projectId]);

  // ── handlers ──────────────────────────────────────────────────
  const handleUploaded = (doc) => {
    setDocuments((prev) => [doc, ...prev]);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document? It will be removed from the AI index.')) return;
    try {
      await documentsAPI.delete(docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      toast.success('Document deleted');
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const handleReindex = async (docId) => {
    try {
      await documentsAPI.reindex(docId);
      // mark locally as unprocessed so polling restarts
      setDocuments((prev) =>
        prev.map((d) => d.id === docId ? { ...d, processed: false, chunk_count: 0, extraction_error: null } : d)
      );
      toast.success('Reindexing started…');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reindex failed');
    }
  };

  const refreshDocs = useCallback(async () => {
    try {
      const res = await documentsAPI.list(projectId);
      setDocuments(res.data);
    } catch {
      toast.error('Failed to refresh documents');
    }
  }, [projectId]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );

  if (!project) return null;

  const indexedCount = documents.filter((d) => d.processed && d.chunk_count > 0).length;
  const processingCount = documents.filter((d) => !d.processed).length;

  return (
    <>
      {/* ── Full-screen overlay ───────────────────────────────────────── */}
      {expanded && (
        <div className="fixed inset-0 z-[100] bg-slate-50 flex flex-col chat-fullscreen-enter">
          {/* Slim top bar: back + project name + close */}
          <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0 shadow-sm">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                title="Back to dashboard"
              >
                <ArrowLeftIcon className="w-4 h-4" />
              </button>
              <div>
                <p className="text-sm font-semibold text-gray-900 leading-tight">{project.name}</p>
                {project.description && (
                  <p className="text-xs text-gray-400 leading-tight">{project.description}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => setExpanded(false)}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-100 border border-gray-200 transition-colors"
              title="Exit full screen (Esc)"
            >
              <ArrowsPointingInIcon className="w-4 h-4" />
              Exit full screen
            </button>
          </div>

          {/* Chat fills everything below the bar */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <div className="h-full max-w-5xl mx-auto px-4 py-4 flex flex-col">
              <div className="flex-1 min-h-0 bg-white rounded-2xl border border-gray-200 p-4 overflow-hidden flex flex-col">
                <ChatInterface
                  projectId={parseInt(projectId)}
                  indexedCount={indexedCount}
                  totalDocs={documents.length}
                  isExpanded={true}
                  onToggleExpand={() => setExpanded(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Normal (non-expanded) layout ──────────────────────────────── */}
      <div className="max-w-7xl mx-auto h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/')}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">{project.name}</h1>
            {project.description && <p className="text-sm text-gray-500">{project.description}</p>}
          </div>
          <button
            onClick={refreshDocs}
            className="text-xs text-gray-400 hover:text-gray-600 px-3 py-1.5 border border-gray-200 rounded-lg"
          >
            Refresh
          </button>
        </div>

        {/* Tab nav */}
        <div className="flex gap-1 mb-4 bg-gray-100 rounded-xl p-1 w-fit">
          <TabBtn
            active={activeTab === 'chat'}
            onClick={() => setActiveTab('chat')}
            icon={<ChatBubbleLeftRightIcon className="w-4 h-4" />}
            label="AI Chat"
            badge={indexedCount > 0 ? `${indexedCount} doc${indexedCount !== 1 ? 's' : ''}` : null}
          />
          <TabBtn
            active={activeTab === 'docs'}
            onClick={() => setActiveTab('docs')}
            icon={<DocumentTextIcon className="w-4 h-4" />}
            label={`Documents (${documents.length})`}
            badge={processingCount > 0 ? `${processingCount} processing` : null}
            badgeColor="yellow"
          />
        </div>

        {/* Content — flex-1 fills all remaining height from the h-full chain */}
        <div className="flex-1 min-h-0 bg-white rounded-2xl border border-gray-200 p-4 overflow-hidden flex flex-col">
          {activeTab === 'chat' && (
            <ChatInterface
              projectId={parseInt(projectId)}
              indexedCount={indexedCount}
              totalDocs={documents.length}
              isExpanded={false}
              onToggleExpand={() => setExpanded(true)}
            />
          )}
          {activeTab === 'docs' && (
            <div className="flex flex-col h-full overflow-hidden gap-4">
              <div className="flex-shrink-0">
                <DocumentUpload projectId={parseInt(projectId)} onUploaded={handleUploaded} />
              </div>
              <div className="flex flex-col flex-1 overflow-hidden min-h-0">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex-shrink-0">
                  Uploaded Documents
                </h3>
                <div className="flex-1 overflow-y-auto pr-1">
                  <DocumentList
                    documents={documents}
                    onDelete={handleDelete}
                    onReindex={handleReindex}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function TabBtn({ active, onClick, icon, label, badge, badgeColor = 'blue' }) {
  const badgeClass = badgeColor === 'yellow'
    ? 'bg-yellow-100 text-yellow-700'
    : 'bg-green-100 text-green-700';
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
        active ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {icon} {label}
      {badge && (
        <span className={`text-xs px-1.5 py-0.5 rounded-full font-normal ${badgeClass}`}>
          {badge}
        </span>
      )}
    </button>
  );
}
